import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';

import { Observable, of } from 'rxjs';
import { take } from 'rxjs/operators';

import { ConfigFacade } from '@rx-applications/clientsite/shared/config/data-access';
import { RouterFacade } from '@rx-applications/clientsite/shared/router/data-access';
import { Filter, FilterGroup, FilterPageSource, PriceRangeChange } from '@rx-applications/clientsite/search/shared';
import { FilterService } from '@rx-applications/clientsite/search/filter/api';
import { AmenityName, FreeCancellationFilter, PropertyTypeName } from '@rx-applications/clientsite/shared/utils';
import { CheckboxChangeEvent, Interaction } from '@roomex/rx-components';
import { FeatureFlagFacade } from '@rx-applications/shared/clientsite/feature-flags';
import { LoggingFacade } from '@rx-applications/clientsite/core/logging';
import { ResultsFacade } from '@rx-applications/clientsite/search/location/api';
import { SearchFacade } from '@rx-applications/clientsite/search/searchbox/api';
import { AllFiltersListComponent } from '@rx-applications/clientsite/search/ui';
import { AsyncPipe } from '@angular/common';
import { StarRatingsAdvanced } from '@rx-applications/shared/utils';
import { FilterFacade } from '@rx-applications/clientsite/search/filter/data-access';

@Component({
  selector: 'rx-applications-filter-panel',
  templateUrl: './feature-filter-panel.component.html',
  styleUrls: ['./feature-filter-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AllFiltersListComponent, AsyncPipe],
  standalone: true,
})
export class FeatureFilterPanelComponent implements OnInit {
  popularFiltersList$: Observable<(Filter | FilterGroup)[]> = of([]);
  otherFiltersList$: Observable<(Filter | FilterGroup)[]> = of([]);
  parkingFiltersList$: Observable<(Filter | FilterGroup)[]> = of([]);
  freeCancellationFilter$: Observable<Filter>;
  mealOptionFilters: Filter[];
  loyaltyProgramFilter$: Observable<Filter>;

  constructor(
    public filterService: FilterService,
    public routerFacade: RouterFacade,
    public resultsFacade: ResultsFacade,
    public filterFacade: FilterFacade,
    public configFacade: ConfigFacade,
    public loggingFacade: LoggingFacade,
    public searchFacade: SearchFacade,
    public featureFlagFacade: FeatureFlagFacade,
  ) {}

  ngOnInit() {
    this.popularFiltersList$ = this.filterService.popularFilterList$;
    this.otherFiltersList$ = this.filterService.otherFilterList$;
    this.parkingFiltersList$ = this.filterService.parkingFilterList$;
    this.freeCancellationFilter$ = this.filterService.freeCancellationFilter$;
    this.loyaltyProgramFilter$ = this.filterService.loyaltyProgramFilter$;
    this.searchFacade.mealOptionFilters$.pipe(take(1)).subscribe((value) => (this.mealOptionFilters = value));
  }

  onPropertyTypeSelect(checkboxEvent: CheckboxChangeEvent) {
    this.filterFacade.setPropertyTypeFilter(checkboxEvent);
  }

  onSelectAllPropertyTypeSelect(checkboxEventList: CheckboxChangeEvent[]) {
    checkboxEventList?.forEach((x) => this.filterFacade.setPropertyTypeFilter(x));
    this.filterFacade.includeNotRatedPropertiesFilter(checkboxEventList[0].value as PropertyTypeName);
  }

  onStarSelect(changes: StarRatingsAdvanced[]) {
    const checkboxesSelected: StarRatingsAdvanced[] = changes.filter((checkbox) => checkbox.isSelected);
    if (checkboxesSelected.length > 0) {
      const stars = checkboxesSelected.map((checkbox) => checkbox.numberOfStars).sort();
      this.filterFacade.setStarFilter(stars);
    }
  }

  onAmenitySelect(amenity: CheckboxChangeEvent) {
    this.filterFacade.setAmenityFilter(<AmenityName>amenity.value, amenity.checked);
  }

  onMealOptionSelect(filtersState: Filter[], { checked, value }: CheckboxChangeEvent) {
    const mealOption = this.filterService.getMealOption(filtersState, checked, value);
    return this.filterFacade.setMealOptionFilter(mealOption);
  }

  onRateFilterSelect(selected: { checked: boolean }) {
    const freeCancellation = selected.checked ? FreeCancellationFilter.FreeCancellation : FreeCancellationFilter.All;
    this.filterFacade.setFreeCancellationFilter(freeCancellation);
    this.loggingFacade.changedFilter({
      freecancellation: selected.checked,
      page: FilterPageSource.LocationResults,
    });
  }

  onPriceRangeFilterChange(priceRangeChange: PriceRangeChange) {
    this.filterFacade.setPriceRangeFilter(priceRangeChange);
  }

  onLoyaltyProgramFilterSelect(value: CheckboxChangeEvent) {
    this.filterFacade.setLoyaltyGuaranteedFilter(value.checked);
    this.loggingFacade.changedFilter({
      loyalty: value.checked,
      page: FilterPageSource.LocationResults,
    });
  }

  onPriceRangeInteractions(props: Interaction): void {
    this.loggingFacade.priceRangeFilterInteractions(props);
  }
}
